import React, { ReactNode, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import { MultibankingApiProvider } from "@api";
import { BicSelect } from "./BicSelect";
import { BankSelectView } from "./BankSelectView";
import { NotSupported } from "./notSupported";
import { GroupedBank, ShortBank } from "./utils";

const Container = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

// A crossfade (no horizontal movement) keeps the step transition smooth without
// pushing content outside the modal, which previously caused a transient
// horizontal scrollbar to flicker in and out while changing steps.
const variants = {
  initial: { opacity: 0 },
  in: { opacity: 1 },
  out: { opacity: 0 },
};

const transition = {
  type: "tween" as const,
  ease: "easeInOut" as const,
  duration: 0.2,
};

const Step = ({
  stepKey,
  children,
}: {
  stepKey: string;
  children: ReactNode;
}) => (
  <motion.div
    key={stepKey}
    initial="initial"
    animate="in"
    exit="out"
    variants={variants}
    transition={transition}
    style={{ width: "100%" }}
  >
    {children}
  </motion.div>
);

const Subtitle = ({ children }: { children: ReactNode }) => (
  <Typography variant="body1" color="textSecondary" mb={6}>
    {children}
  </Typography>
);

export interface BankSelectContentProps {
  onSelect: (
    id: number | undefined,
    multibankingApiProvider: MultibankingApiProvider,
    wealthApiSlug: string | undefined
  ) => void;
  searchValue: string;
  className?: string;
  onChange?: (value: string) => void;
  banks: GroupedBank[];
  isFetching: boolean;
  onClose?: () => void;
}

export const BankSelectContent = ({
  onSelect,
  className,
  banks,
  isFetching,
  searchValue,
  onChange,
  onClose,
}: BankSelectContentProps) => {
  const [selectedBank, setSelectedBank] = useState<GroupedBank | undefined>();
  const [showNotSupported, setShowNotSupported] = useState(false);
  const [showBicSelect, setShowBicSelect] = useState(false);

  const { t } = useTranslation();

  const handleBicSelect = (bankByBic: ShortBank) => {
    if (!bankByBic.isSupported) {
      setShowNotSupported(true);
      return;
    }
    onSelect(bankByBic.id, bankByBic.multibankingApiProvider, bankByBic.slug);
  };

  const handleSelect = (groupedBank: GroupedBank) => {
    setSelectedBank(groupedBank);
    if (groupedBank.banks.length === 1) {
      handleBicSelect(groupedBank.banks[0]);
      return;
    }
    setShowBicSelect(true);
  };

  const handleBicSelectCancel = () => {
    setShowBicSelect(false);
    setSelectedBank(undefined);
  };

  const handleBackFromNotSupported = () => {
    setShowNotSupported(false);
  };

  const handleBack = () => {
    if (showNotSupported) {
      handleBackFromNotSupported();
    } else if (showBicSelect) {
      handleBicSelectCancel();
    } else {
      onClose?.();
    }
  };

  return (
    <Container className={className}>
      <AnimatePresence initial={false} mode="wait">
        {!showBicSelect && !showNotSupported && (
          <Step stepKey="bank-select">
            <Subtitle>{t("bankSelect.title")}</Subtitle>
            <BankSelectView
              isFetching={isFetching}
              banks={banks}
              onChange={onChange}
              searchValue={searchValue}
              onSelect={handleSelect}
              shouldDisplayTitle={false}
            />
          </Step>
        )}
        {selectedBank && !showNotSupported && showBicSelect && (
          <Step stepKey="bic-select">
            <Subtitle>{t("bankSelect.selectBic.title")}</Subtitle>
            <BicSelect
              className="bicSelect"
              banks={selectedBank.banks || []}
              onSelect={handleBicSelect}
            />
          </Step>
        )}
        {showNotSupported && selectedBank && (
          <Step stepKey="not-supported">
            <NotSupported
              className="notSupported"
              bankName={selectedBank?.name}
              iconUrl={selectedBank?.iconUrl}
            />
          </Step>
        )}
      </AnimatePresence>
      <Box mt={6}>
        <Button
          className="bankSelectBackButton"
          variant="secondary"
          data-testid="back-button"
          onClick={handleBack}
        >
          {t("bankSelect.selectBic.back")}
        </Button>
      </Box>
    </Container>
  );
};
